import axios from 'axios'
import { sumBy } from 'lodash-es'
import { EBO_API } from '~/utils/constants'
import { ETH_TOKEN, getBalance } from '~/services/ethereum'
import eboInstance from '~/services/ebo'

const axiosInstance = axios.create({
  baseURL: 'https://stats.ebo.io',
})

export const getAccountData = async (address, txLimit = 30, block) => {
  let blockParam = ''
  if (Number.isInteger(block)) {
    blockParam = `&block=${block}`
  }

  const { data } = await axios.get(
    `${EBO_API}/accounts/${address}?currency=USD&limit=${txLimit}${blockParam}`
  )
  return data
}

// TODO: More Currencies
export const fetchCoinPriceFull = (coinSymbol) => {
  const symbols = Array.isArray(coinSymbol) ? coinSymbol.join(',') : coinSymbol
  return axiosInstance
    .get(`/data/pricemultifull?fsyms=${symbols}&tsyms=USD`)
    .then(({ data }) => data)
}

export const getSupportedTokens = () =>
  eboInstance.get('/tokens/new').then(({ data }) => data)

export const getNews = async (tokenSymbol) => {
  const { data } = await axiosInstance.get(
    `/data/v2/news/?lang=EN&categories=${tokenSymbol}`
  )
  const { decode } = await import('html-entities')
  return data.Data.map((newsdata) => ({
    ...newsdata,
    guidUrl: newsdata.guid,
    body: decode(newsdata.body),
    categories: newsdata.categories.replaceAll('|', ', '),
  }))
}

/**
 *  Formats dates like `20220417` from the API
 */
// TODO: use a date library
export function toDate(dateStr) {
  const year = dateStr.slice(0, 4)
  const month = dateStr.slice(4, 6)
  const day = dateStr.slice(6, 8)

  return new Date(year, month - 1, day)
}

export async function priceHistory(coinSymbol) {
  const extractCandleValues = ({ data }) => {
    return data.Data.Data?.map((candle) => ({
      time: candle.time,
      value: candle.close,
    }))
  }

  const apiUrls = [
    `/histohour?fsym=${coinSymbol}&limit=24&tsym=USD`,
    `/histohour?fsym=${coinSymbol}&tsym=USD&limit=${24 * 7}&aggregate=3`,
    `/histohour?fsym=${coinSymbol}&tsym=USD&limit=${24 * 7 * 30}&aggregate=5`,
    `/histoday?fsym=${coinSymbol}&tsym=USD&limit=365&tosym=USD`,
  ]

  const [dayData, weekData, monthData, yearData] = await Promise.all(
    apiUrls.map((url) => axiosInstance.get(`/data/v2${url}`).then(extractCandleValues))
  )

  return {
    dayData,
    weekData,
    monthData,
    yearData,
  }
}

export async function getValueData(portfolio, walletAddress) {
  portfolio = [...portfolio, ETH_TOKEN]
  const [balances, tokenPrices, { default: BigNumber }] = await Promise.all([
    Promise.all(
      portfolio.map((token) => getBalance(token.address, walletAddress, token.decimals))
    ),
    fetchCoinPriceFull(portfolio.map((token) => token.symbol)).then(({ RAW }) => RAW),
    import('bignumber.js'),
  ])

  portfolio = portfolio.map((token, i) => {
    if (!(token.symbol in tokenPrices)) {
      console.info("counldn't find", token.address)

      return {
        ...token,
        priceUsd: null,
        balanceStr: null,
        balanceUsd: null,
        changePercent24Hr: null,
        changeUsd24Hr: null,
      }
    }

    const priceData = tokenPrices[token.symbol].USD

    // They have their own percent change field as well,
    // but it's calculated differently (+24% would get returned as 1.24)
    const changePercent24Hr = priceData.CHANGE24HOUR / priceData.OPEN24HOUR
    const balanceUsd = BigNumber(balances[i]).times(priceData.PRICE).toNumber()
    const initialBalanceUsd24Hr = BigNumber(balances[i])
      .times(priceData.OPEN24HOUR)
      .toNumber()
    const changeUsd24Hr = Math.abs(balanceUsd - initialBalanceUsd24Hr)

    return {
      ...token,
      priceUsd: priceData.PRICE,
      balanceStr: balances[i],
      initialBalanceUsd24Hr,
      balanceUsd,
      changePercent24Hr,
      changeUsd24Hr,
    }
  })

  const value = sumBy(portfolio, (token) => token.balanceUsd || 0)
  const initialValue = sumBy(portfolio, (token) => token.initialBalanceUsd24Hr || 0)
  const change24Hr = (value - initialValue) / initialValue

  return {
    portfolio,
    value,
    change24Hr,
  }
}

export function getPriceAtDate(symbol, date) {
  return axiosInstance
    .get(
      `/data/pricehistorical?fsym=${symbol}&tsyms=USD&ts=${Math.floor(
        date.getTime() / 1000
      )}`
    )
    .then(({ data }) => data[symbol].USD)
}

// TODO: Impliment it this way.
// Plan for not calling all at once
export function priceHistorySingle(symbol, interval = 'day') {
  // TODO replace with env variable.
  const priceIntervals = {
    day: `/data/v2/histohour?fsym=${symbol}&limit=24&tsym=USD`,
    week: `/data/v2/histohour?fsym=${symbol}&tsym=USD&limit=${24 * 7}&aggregate=3`,
    month: `/data/v2/histohour?fsym=${symbol}&tsym=USD&limit=${24 * 7 * 30}&aggregate=5`,
    year: `/data/v2/histoday?fsym=${symbol}&tsym=USD&limit=365&tosym=USD`,
  }
  if (!priceIntervals[interval])
    throw new Error(`Invalid interval ${interval}. [day, week, month, year]`)
  return axiosInstance.get(priceIntervals[interval]).then((res) => {
    return res.data.Data.Data.map((candle) => ({
      time: candle.time,
      value: candle.close,
    }))
  })
  // return Object.keys(priceIntervals).reduce((acc, val) => {
  //   acc[`${val}Data`] = interval === 'val' ? activeData : []
  //   return acc
  // }, {})

  // TODO: this seems like it was designed based on the graph module used
  // TODO: simplify result
  // return {
  //   dayData : (interval === 'day')? activeData ,
  //   weekData,
  //   monthData,
  //   yearData,
  // }
}
